import React, { useState } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import BillingSystem from './components/BillingSystem';
import OrderTracking from './components/OrderTracking';
import LiveDashboard from './components/LiveDashboard';
import InventoryManagement from './components/InventoryManagement';
import Reports from './components/Reports';
import { LayoutDashboard, Receipt, ChefHat, Package, FileText, LogOut, User, Menu, X } from 'lucide-react';
import { Button } from './components/ui/button';
import './index.css';

function MainApp() {
  const { user, login, logout, loading, hasPermission, isAuthenticated } = useAuth();
  const [activeMenu, setActiveMenu] = useState('billing');
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Show loading screen while verifying token
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLogin={login} />;
  }

  // Menu items with permission requirements
  const menuItems = [
    { id: 'live-dashboard', label: 'Dashboard', icon: LayoutDashboard, permission: 'live-dashboard' },
    { id: 'billing', label: 'Bill & Payment', icon: Receipt, permission: 'billing' },
    { id: 'orders', label: 'Kitchen KOT', icon: ChefHat, permission: 'orders' },
    { id: 'inventory', label: 'Inventory', icon: Package, permission: 'inventory' },
    { id: 'reports', label: 'Reports', icon: FileText, permission: 'reports' },
  ].filter(item => hasPermission(item.permission));

  // Set default menu to first available item
  if (menuItems.length > 0 && !menuItems.find(item => item.id === activeMenu)) {
    setActiveMenu(menuItems[0].id);
  }

  const handleLogout = async () => {
    if (window.confirm('Are you sure you want to logout?')) {
      await logout();
    }
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      admin: 'bg-orange-600',
      manager: 'bg-blue-600',
      billing: 'bg-green-600',
      chef: 'bg-purple-600'
    };
    return colors[role] || 'bg-gray-600';
  };

  const renderPage = () => {
    switch (activeMenu) {
      case 'billing':
        return hasPermission('billing') ? <BillingSystem setActiveMenu={setActiveMenu} /> : null;
      case 'orders':
        return hasPermission('orders') ? <OrderTracking /> : null;
      case 'live-dashboard':
        return hasPermission('live-dashboard') ? <LiveDashboard /> : null;
      case 'inventory':
        return hasPermission('inventory') ? <InventoryManagement /> : null;
      case 'reports':
        return hasPermission('reports') ? <Reports /> : null;
      default:
        return menuItems.length > 0 ? renderPage() : (
          <div className="flex items-center justify-center h-full">
            <p className="text-gray-500">No accessible pages</p>
          </div>
        );
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        } ${!sidebarExpanded && 'lg:w-20'}`}
      >
        <div className="flex items-center justify-between p-4 lg:p-6 border-b border-gray-200">
          {sidebarExpanded && (
            <h1 className="text-xl font-bold text-orange-600">Restaurant POS</h1>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarExpanded(!sidebarExpanded)}
            className="lg:ml-auto hidden lg:flex"
          >
            {sidebarExpanded ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(false)}
            className="lg:hidden"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* User Info */}
        {sidebarExpanded && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <User className="text-orange-600" size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.full_name}</p>
                <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold text-white ${getRoleBadgeColor(user?.role)} mt-0.5`}>
                  {user?.role?.toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        )}

        <nav className="px-3 py-4 space-y-1 flex-1 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                onClick={() => {
                  setActiveMenu(item.id);
                  if (window.innerWidth < 1024) {
                    setMobileMenuOpen(false);
                  }
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                  activeMenu === item.id
                    ? 'bg-orange-50 text-orange-600'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                title={!sidebarExpanded ? item.label : ''}
              >
                <Icon className="w-5 h-5 flex-shrink-0" />
                {sidebarExpanded && <span className="text-sm font-medium">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        <div className="p-3 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
            title={!sidebarExpanded ? 'Logout' : ''}
          >
            <LogOut className="w-5 h-5 flex-shrink-0" />
            {sidebarExpanded && <span className="text-sm font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        {/* Mobile Header */}
        <div className="lg:hidden sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(true)}
          >
            <Menu className="w-5 h-5" />
          </Button>
          <h2 className="text-base font-semibold text-gray-900">
            {menuItems.find(nav => nav.id === activeMenu)?.label}
          </h2>
          <div className="w-10" /> {/* Spacer for alignment */}
        </div>
        {renderPage()}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <MainApp />
    </AuthProvider>
  );
}

export default App;
